import React, { useState, useEffect } from "react";
import {
	Modal,
	Form,
	Input,
	Select,
	DatePicker,
	InputNumber,
	Button,
	Table,
	Tag,
	message,
	Row,
	Col,
	Typography,
} from "antd";
import { PlusOutlined, UserOutlined } from "@ant-design/icons";
import useAxios from "../../utilities/useAxios";
import dayjs from "dayjs";
import useIsMobile from "../../hooks/useIsMobile";

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const AddDisciplinaryRecordModal = ({ visible, onCancel, onSuccess }) => {
	const isMobile = useIsMobile();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [members, setMembers] = useState([]);
	const [disciplinaryTypes, setDisciplinaryTypes] = useState([]);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [searchText, setSearchText] = useState("");
	const [searchCategory, setSearchCategory] = useState("name");

	useEffect(() => {
		if (visible) {
			fetchMembers();
			fetchDisciplinaryTypes();
		}
	}, [visible]);

	const fetchMembers = async () => {
		try {
			const response = await useAxios.get(
				"/users/all-users/"
			);
			if (response.status === 200) {
				setMembers(response.data);
			}
		} catch (error) {
			message.error("회원 목록을 불러오는데 실패했습니다.");
		}
	};

	const fetchDisciplinaryTypes = async () => {
		try {
			const response = await useAxios.get(
				"/users/disciplinary-types/"
			);
			if (response.status === 200) {
				setDisciplinaryTypes(response.data);
			}
		} catch (error) {
			message.error("징계 유형을 불러오는데 실패했습니다.");
		}
	};

	const handleSearch = (value) => {
		setSearchText(value);
	};

	const handleCategoryChange = (value) => {
		setSearchCategory(value);
	};

	const handleMemberSelection = (selectedRowKeys, selectedRows) => {
		// Get all currently selected members (including those not in current filtered view)
		const allSelectedMembers = [...selectedMembers];

		// Update the selection for the current filtered members
		selectedRows.forEach((selectedRow) => {
			const existingIndex = allSelectedMembers.findIndex(
				(member) => member.id === selectedRow.id
			);
			if (existingIndex === -1) {
				// Add new selection
				allSelectedMembers.push(selectedRow);
			}
		});

		// Remove deselected members from current filtered view
		const currentFilteredIds = filteredMembers.map(
			(member) => member.id
		);
		const finalSelectedMembers = allSelectedMembers.filter(
			(member) => {
				// Keep if it's in the current filtered view and selected
				if (currentFilteredIds.includes(member.id)) {
					return selectedRowKeys.includes(
						member.id
					);
				}
				// Keep if it's not in current filtered view (preserve previous selections)
				return true;
			}
		);

		setSelectedMembers(finalSelectedMembers);
	};

	const handleSubmit = async (values) => {
		if (selectedMembers.length === 0) {
			message.error("최소 한 명의 회원을 선택해주세요.");
			return;
		}

		try {
			setLoading(true);
			const submitData = {
				...values,
				user_ids: selectedMembers.map(
					(member) => member.id
				),
				resolution_date:
					values.resolution_date.format(
						"YYYY-MM-DD"
					),
				start_date: values.start_date.format(
					"YYYY-MM-DD"
				),
			};

			const response = await useAxios.post(
				"/users/add-disciplinary-record/",
				submitData
			);

			if (response.status === 201) {
				message.success(
					`${selectedMembers.length}명의 회원에게 징계 기록이 추가되었습니다.`
				);
				form.resetFields();
				setSelectedMembers([]);
				if (onSuccess) {
					onSuccess();
				}
			}
		} catch (error) {
			message.error("징계 기록 추가에 실패했습니다.");
		} finally {
			setLoading(false);
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "입교인":
				return "blue";
			case "학습인":
				return "green";
			case "유아세례교인":
				return "orange";
			case "실종교인":
				return "red";
			case "별세교인":
				return "default";
			default:
				return "default";
		}
	};

	const getClassificationColor = (classification) => {
		switch (classification) {
			case "성도":
				return "blue";
			case "교직원":
				return "green";
			case "교역자":
				return "purple";
			case "은퇴목사":
				return "orange";
			default:
				return "default";
		}
	};

	const getDisciplinaryColor = (type) => {
		switch (type) {
			case "출교":
				return "red";
			case "파면":
				return "volcano";
			case "정직":
				return "orange";
			case "감봉":
				return "gold";
			case "견책":
				return "lime";
			case "근신":
				return "blue";
			default:
				return "default";
		}
	};

	const filteredMembers = members.filter((member) => {
		if (searchText) {
			const searchValue =
				member[searchCategory]
					?.toString()
					.toLowerCase() || "";
			if (!searchValue.includes(searchText.toLowerCase())) {
				return false;
			}
		}
		return true;
	});

	const memberColumns = [
		{
			title: "이름",
			dataIndex: "name",
			key: "name",
			render: (text) => text || "-",
		},
		{
			title: "생년월일",
			dataIndex: "birthdate",
			key: "birthdate",
			render: (text) =>
				text ? dayjs(text).format("YYYY-MM-DD") : "-",
		},
		{
			title: "성별",
			dataIndex: "gender",
			key: "gender",
			render: (text) => text || "-",
		},
		{
			title: "분류",
			dataIndex: "classification_name",
			key: "classification_name",
			render: (text) => (
				<Tag color={getClassificationColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "상태",
			dataIndex: "status_name",
			key: "status_name",
			render: (text) => (
				<Tag color={getStatusColor(text)}>
					{text || "-"}
				</Tag>
			),
		},
		{
			title: "소속",
			dataIndex: "affiliated_group",
			key: "affiliated_group",
			render: (text) => text || "-",
		},
	];

	return (
		<Modal
			title={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "8px",
					}}>
					<PlusOutlined />
					<span>징계 기록 추가</span>
				</div>
			}
			open={visible}
			onCancel={onCancel}
			footer={null}
			width="90%"
			style={{ top: 20 }}>
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={{
					resolution_date: dayjs(),
					start_date: dayjs(),
					period: 1,
				}}>
				{/* Member Selection Section */}
				<div style={{ marginBottom: 24 }}>
					<Title level={4}>
						<UserOutlined /> 회원 선택
					</Title>
					<Text type="secondary">
						징계 기록을 추가할 회원을
						선택하세요.
					</Text>

					{/* Search Section */}
					<Row
						gutter={[16, 16]}
						style={{
							marginTop: 16,
							marginBottom: 16,
						}}>
						<Col xs={24} sm={12} md={6}>
							<Select
								defaultValue="name"
								style={{
									width: "100%",
								}}
								onChange={
									handleCategoryChange
								}
								options={[
									{
										value: "name",
										label: "이름",
									},
									{
										value: "phone_number",
										label: "연락처",
									},
									{
										value: "affiliated_group",
										label: "소속",
									},
								]}
							/>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Input.Search
								placeholder="검색어를 입력하세요"
								allowClear
								onSearch={
									handleSearch
								}
								style={{
									width: "100%",
								}}
							/>
						</Col>
					</Row>

					{/* Member Table */}
					<Table
						columns={memberColumns}
						dataSource={filteredMembers}
						rowKey="id"
						rowSelection={{
							type: "checkbox",
							selectedRowKeys:
								selectedMembers.map(
									(
										member
									) =>
										member.id
								),
							onChange: handleMemberSelection,
						}}
						pagination={{
							pageSize: 10,
							showSizeChanger: true,
							showQuickJumper: true,
						}}
						scroll={{ y: 300 }}
					/>

					{selectedMembers.length > 0 && (
						<div style={{ marginTop: 16 }}>
							<Text strong>
								선택된 회원:{" "}
								{
									selectedMembers.length
								}
								명
							</Text>
							<div
								style={{
									marginTop: 8,
								}}>
								{selectedMembers.map(
									(
										member
									) => (
										<Tag
											key={
												member.id
											}
											style={{
												marginBottom: 4,
											}}>
											{
												member.name
											}
										</Tag>
									)
								)}
							</div>
						</div>
					)}
				</div>

				{/* Disciplinary Information Section */}
				<div style={{ marginBottom: 24 }}>
					<Title level={4}>
						<PlusOutlined /> 징계 정보
					</Title>
					<Text type="secondary">
						징계 정보를 입력하세요.
					</Text>

					<Row
						gutter={[16, 16]}
						style={{ marginTop: 16 }}>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="type"
								label="징계 유형"
								rules={[
									{
										required: true,
										message: "징계 유형을 선택하세요",
									},
								]}>
								<Select placeholder="징계 유형 선택">
									{disciplinaryTypes.map(
										(
											type
										) => (
											<Option
												key={
													type.id
												}
												value={
													type.name
												}>
												<Tag
													color={getDisciplinaryColor(
														type.name
													)}>
													{
														type.name
													}
												</Tag>
											</Option>
										)
									)}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="resolution_date"
								label="결의일"
								rules={[
									{
										required: true,
										message: "결의일을 선택하세요",
									},
								]}>
								<DatePicker
									inputReadOnly={isMobile}
									style={{
										width: "100%",
									}}
									format="YYYY-MM-DD"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="start_date"
								label="징계 시작일"
								rules={[
									{
										required: true,
										message: "징계 시작일을 선택하세요",
									},
								]}>
								<DatePicker
									inputReadOnly={isMobile}
									style={{
										width: "100%",
									}}
									format="YYYY-MM-DD"
								/>
							</Form.Item>
						</Col>
						<Col xs={24} sm={12} md={6}>
							<Form.Item
								name="period"
								label="징계 기간 (일)"
								rules={[
									{
										required: true,
										message: "징계 기간을 입력하세요",
									},
								]}>
								<InputNumber
									min={1}
									style={{
										width: "100%",
									}}
									placeholder="기간 입력"
								/>
							</Form.Item>
						</Col>
					</Row>

					<Form.Item
						name="reason"
						label="징계 사유"
						rules={[
							{
								required: true,
								message: "징계 사유를 입력하세요",
							},
						]}>
						<TextArea
							rows={4}
							placeholder="징계 사유를 상세히 입력하세요"
						/>
					</Form.Item>
				</div>

				{/* Submit Button */}
				<div style={{ textAlign: "right" }}>
					<Button
						type="primary"
						htmlType="submit"
						loading={loading}
						icon={<PlusOutlined />}
						disabled={
							selectedMembers.length ===
							0
						}>
						징계 기록 추가
					</Button>
				</div>
			</Form>
		</Modal>
	);
};

export default AddDisciplinaryRecordModal;
